import { NeckDiagramPlaybackProvider } from "./neckDiagrams/NeckDiagramViewing.js";
import { NeckDiagramStudentView } from "./neckDiagrams/NeckDiagramStudentView.js";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import {
  NavLink,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { get, type Me, type MyStudent } from "./api.js";
import { FamilyBooking } from "./booking/FamilyBooking.js";
import { StudentBooking } from "./booking/StudentDashboard.js";
import { FAMILY_HISTORY_LABEL, History } from "./booking/History.js";
import { Lessons } from "./booking/Lessons.js";
import { Sidebar, type SidebarItem } from "./Sidebar.js";
import { useAuth } from "./auth.js";
import { Inbox } from "./inbox/Inbox.js";
import { InboxReplyDraftProvider } from "./inbox/drafts.js";
import { InboxSidebarBadge, InboxUnreadProvider } from "./inbox/unread.js";
import {
  NotificationBell,
  Notifications,
  NotificationsProvider,
} from "./notifications/Bell.js";
import { Accessibility } from "./pages/Accessibility.js";
import { Account } from "./pages/Account.js";
import { FamilyInvoices } from "./pages/FamilyInvoices.js";
import { GroupActivities } from "./pages/GroupActivities.js";
import { FamilyProgress } from "./pages/FamilyProgress.js";
import { FamilyRepertoireMaterial } from "./pages/FamilyRepertoireMaterial.js";
import { FamilyPurchaseNotice } from "./FamilyPurchaseNotice.js";
import { NotFoundRecovery } from "./pages/NotFoundRecovery.js";
import {
  Metronome,
  MetronomeProvider,
  PracticeMetronomeShell,
} from "./pages/Metronome.js";
import { FamilyHome, FamilyLessonNotes } from "./pages/MyLessons.js";
import { HouseholdOverview } from "./pages/HouseholdOverview.js";
import { Practice } from "./pages/Practice.js";
import { PracticeMetrics } from "./pages/PracticeMetrics.js";
import { PracticeLearnerContext } from "./practiceInstrument.js";
import { AwardTracker, StudentAwards } from "./awards/StudentAwards.js";
import "./practiceWorkspace.css";
import {
  DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES,
  getStudentAccessibilityPreferences,
  type StudentAccessibilityPreferences,
} from "./studentAccessibilityApi.js";
import {
  hasCustomStudentColours,
  studentAccessibilityStyle,
} from "./studentAccessibilityTheme.js";
import { useAsync } from "./useAsync.js";
import { BugReporter, BugReporterProvider } from "./bugs/BugReporter.js";

// Account setup is occasional; keep it out of the family's initial bundle.
const DrumAssessments = lazy(() => import("./drums/DrumAssessments.js").then(m => ({ default: m.DrumAssessments })));
const FamilySetup = lazy(() => import("./pages/FamilySetup.js").then(module => ({ default: module.FamilySetup })));
const ConcertInvitations = lazy(() => import("./pages/ConcertInvitations.js").then(module => ({ default: module.ConcertInvitations })));
const LinkMyChild = lazy(() => import("./pages/LinkMyChild.js").then(module => ({ default: module.LinkMyChild })));
const ConfirmChildLink = lazy(() => import("./pages/LinkMyChild.js").then(module => ({ default: module.ConfirmChildLink })));
import { neckDiagramCapabilities } from "./neckDiagrams/api.js";
const HelpRoutes = lazy(() => import("./help/Help.js").then(module => ({ default: module.HelpRoutes })));
const PitchPractice = lazy(() => import("./pages/PitchPractice.js").then(module => ({ default: module.PitchPractice })));
const NeckDiagrams = lazy(() => import("./pages/NeckDiagrams.js").then(m => ({ default: m.NeckDiagrams })));
const NeckDiagram = lazy(() => import("./pages/NeckDiagram.js").then(m => ({ default: m.NeckDiagram })));
const SharedNeckDiagram = lazy(() => import("./pages/NeckDiagram.js").then(m => ({ default: m.SharedNeckDiagram })));

const FAMILY_STUDENT_STORAGE_PREFIX = "wmf.family.studentId.";
const LEGACY_FAMILY_STUDENT_STORAGE_KEY = "wmf.family.studentId";

function familyStudentStorageKey(userId: string): string {
  return `${FAMILY_STUDENT_STORAGE_PREFIX}${userId}`;
}

function storedFamilyStudentId(userId: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    window.localStorage.removeItem(LEGACY_FAMILY_STUDENT_STORAGE_KEY);
    return window.localStorage.getItem(familyStudentStorageKey(userId));
  } catch {
    return null;
  }
}

function rememberFamilyStudentId(
  userId: string,
  studentId: string | null,
): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LEGACY_FAMILY_STUDENT_STORAGE_KEY);
    const key = familyStudentStorageKey(userId);
    if (studentId) window.localStorage.setItem(key, studentId);
    else window.localStorage.removeItem(key);
  } catch {
    // Storage can be unavailable in privacy modes. The mounted shell still
    // keeps the selection for this session, so persistence is a progressive
    // enhancement rather than a reason to break family navigation.
  }
}

function forgetFamilyStudentId(userId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(familyStudentStorageKey(userId));
    window.localStorage.removeItem(LEGACY_FAMILY_STUDENT_STORAGE_KEY);
  } catch {
    // Signing out must still work even if browser storage is unavailable.
  }
}

/**
 * Whether to offer this family the booking and payment areas.
 *
 * Both halves have to be true and they answer different questions.
 * `bookingConfigured` is `me.booking.student` — whether the booking
 * integration exists for this school at all. `privateBooking` is per student,
 * and false for a School pupil, whose timetable the school sets: the private
 * routes refuse to act for one, so offering the screens would draw a page
 * whose every call fails. No student selected is also no, which matches what
 * the sections themselves do.
 *
 * Pure and exported so the rule can be read and tested on its own, rather than
 * inferred from the shell.
 */
export function familyBookingEnabled(
  bookingConfigured: boolean,
  student: Pick<MyStudent, "privateBooking"> | null,
): boolean {
  return bookingConfigured && (student?.privateBooking ?? false);
}

/**
 * Whether this family is billed for school teaching.
 *
 * True as soon as any of their children is a School pupil, because an invoice
 * is addressed to the family and covers every child on it — a household with
 * one private and one school child still gets school bills, and hiding the
 * section from them would hide a bill they owe.
 */
export function familyHasSchoolInvoicing(
  students: readonly MyStudent[],
): boolean {
  return students.some((student) => student.schoolInvoicing ?? !student.privateBooking);
}

/** Compact, persistent family destinations use the same gates as their routes. */
export function familyMobileShortcutItems(
  lessonsEnabled: boolean,
  bookingEnabled: boolean,
  hasStudent: boolean,
): SidebarItem[] {
  return [
    { to: "/home", label: "Home" },
    ...(lessonsEnabled && hasStudent ? [{ to: "/lessons", label: "Lessons" }] : []),
    ...(bookingEnabled ? [{ to: "/book", label: "Book" }] : []),
    ...(hasStudent ? [{ to: "/practice", label: "Practice" }] : []),
  ];
}

/** The family areas are data so adding another section does not alter the shell. */
export function familySidebarItems(
  bookingEnabled: boolean,
  hasStudent = true,
  schoolInvoicing = false,
  lessonsEnabled = bookingEnabled,
  awardsEnabled = false,
  neckDiagramsEnabled = false,
  concertInvitationsEnabled = false,
  groupActivitiesEnabled = false,
): SidebarItem[] {
  const items: SidebarItem[] = [
    { to: "/home", label: "Home" },
    // The old per-student "Updates" panel became a destination of its own,
    // named the same thing here as it is in the Staff Hub. A family with no
    // linked student still gets it: an announcement is addressed to the
    // account rather than to a child.
    { to: "/inbox", label: "Inbox", badge: <InboxSidebarBadge /> },
  ];
  if (concertInvitationsEnabled) items.push({ to: "/concert-invitations", label: "Concert invitations" });
  if (groupActivitiesEnabled && hasStudent) items.push({ to: "/activities", label: "Group activities" });

  if (bookingEnabled) {
    items.push({ to: "/book", label: "Book a lesson" });
  }

  if (lessonsEnabled && hasStudent) {
    items.splice(2, 0, { to: "/lessons", label: "Lessons" });
  }

  if (bookingEnabled) {
    items.push({ to: "/history", label: FAMILY_HISTORY_LABEL });
  }

  // School invoices are a separate section from "Payments and requests", which
  // is Amelia's private credit and payment history. They are different money
  // with different rules, and a family can legitimately have both.
  if (schoolInvoicing) items.push({ to: "/invoices", label: "Invoices" });

  items.push({ to: "/practice", label: "Practice" });
  if (hasStudent) items.push({ to: "/practice-history", label: "Practice history" },{to:"/practice-leaderboard",label:"Leaderboard"});
  if (neckDiagramsEnabled) items.push({ to: "/neck-diagrams", label: "My diagrams" });
  if (awardsEnabled && hasStudent) items.push({ to: "/awards", label: "Awards" });
  if (hasStudent) items.push({ to: "/progress", label: "Progress" });
  items.push({ to: "/notes", label: "Lesson notes" });
  // Accessibility changes the selected student's whole Hub presentation, so it
  // remains a first-class student workspace rather than an account/footer
  // utility. With no linked student the route has nothing to configure.
  if (hasStudent) items.push({ to: "/accessibility", label: "Accessibility" });

  items.push({ to: "/help", label: "Help & guides" });
  return items;
}

/** Practice remains one primary workspace; tools live inside it as subsections. */
export function PracticeSectionNav() {
  return (
    <nav className="practice-section-nav" aria-label="Practice sections">
      <NavLink
        end
        to="/practice"
        className={({ isActive }) => (isActive ? "is-active" : undefined)}
      >
        Practice log
      </NavLink>
      <NavLink
        to="/practice/metronome"
        className={({ isActive }) => (isActive ? "is-active" : undefined)}
      >
        Metronome
      </NavLink>
      <NavLink to="/practice/drums" className={({ isActive }) => isActive ? "is-active" : undefined}>Drum assessments</NavLink>
      <NavLink to="/practice/pitch" className={({ isActive }) => (isActive ? "is-active" : undefined)}>Pitch practice</NavLink>
    </nav>
  );
}

export function FamilyMetronomeBoundary({
  studentId,
  children,
}: {
  studentId: string | null;
  children: ReactNode;
}) {
  if (!studentId) return <>{children}</>;
  return (
    <MetronomeProvider key={studentId} studentId={studentId}>
      <PracticeMetronomeShell>{children}</PracticeMetronomeShell>
    </MetronomeProvider>
  );
}

interface StudentAppearanceState {
  studentId: string | null;
  preferences: StudentAccessibilityPreferences;
  loading: boolean;
  error: string | null;
}

function FamilyRouteGate({
  allowed,
  children,
  error,
  loading,
}: {
  allowed: boolean;
  children: ReactNode;
  error: string | null;
  loading: boolean;
}) {
  if (loading)
    return (
      <p className="empty" role="status">
        Loading your students…
      </p>
    );
  if (error) return null;
  return allowed ? children : <Navigate to="/home" replace />;
}

/**
 * The shell owns the effective presentation rather than the settings page.
 *
 * On a child switch the old child's appearance is dropped synchronously to the
 * neutral defaults before the next request completes. That is deliberately a
 * neutral flash rather than a leak of one child's accessibility choices into
 * another child's Hub.
 */
export function useStudentAppearance(studentId: string | null) {
  const [nonce, setNonce] = useState(0);
  const [state, setState] = useState<StudentAppearanceState>({
    studentId: null,
    preferences: { ...DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES },
    loading: false,
    error: null,
  });

  useEffect(() => {
    let current = true;

    if (!studentId) {
      setState({
        studentId: null,
        preferences: { ...DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES },
        loading: false,
        error: null,
      });
      return () => {
        current = false;
      };
    }

    setState({
      studentId,
      preferences: { ...DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES },
      loading: true,
      error: null,
    });

    getStudentAccessibilityPreferences(studentId)
      .then((result) => {
        if (!current) return;
        setState({
          studentId,
          preferences: result.preferences,
          loading: false,
          error: null,
        });
      })
      .catch((cause: unknown) => {
        if (!current) return;
        setState({
          studentId,
          preferences: { ...DEFAULT_STUDENT_ACCESSIBILITY_PREFERENCES },
          loading: false,
          error:
            cause instanceof Error
              ? cause.message
              : "Could not load display settings.",
        });
      });

    return () => {
      current = false;
    };
  }, [studentId, nonce]);

  return {
    ...state,
    apply: (preferences: StudentAccessibilityPreferences) => {
      if (!studentId) return;
      setState({ studentId, preferences, loading: false, error: null });
    },
    reload: () => setNonce((value) => value + 1),
  };
}

/**
 * The persistent family chrome: one sidebar, one child selection, many routes.
 *
 * The child switcher lives here rather than inside each page so changing child
 * changes the subject of every section without duplicating the control or
 * letting one section drift from another.
 */
export function FamilyLayout({ onSignOut }: { onSignOut: () => void }) {
  const { me } = useAuth();
  const { pathname } = useLocation();
  /**
   * A deep link may name the child it is about.
   *
   * A notification about one child is useless on another child's Home — the
   * offer card, the lessons and the practice log all filter to whoever is
   * selected. `?student=` lets a bell say which, and it is honoured only when
   * the account is actually linked to that child, so the parameter widens
   * nothing.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStudentId = searchParams.get("student");
  const accountId = me?.user.id ?? null;
  // A focus refresh can publish changed relationships while this shell stays
  // mounted. Read the authorised learner list again for that new scope.
  const relationshipKey = JSON.stringify([
    [...(me?.studentIds ?? [])].sort(), [...(me?.childStudentIds ?? [])].sort(),
  ]);
  const neckCapabilities = useAsync(neckDiagramCapabilities, [accountId, relationshipKey]);
  const people = useAsync(
    () => get<{ students: MyStudent[] }>("/me/students"),
    [accountId, relationshipKey],
  );
  const [selection, setSelection] = useState<{
    accountId: string | null;
    studentId: string | null;
  }>(() => ({
    accountId,
    studentId: accountId ? storedFamilyStudentId(accountId) : null,
  }));

  useEffect(() => {
    if (selection.accountId === accountId) return;
    setSelection({
      accountId,
      studentId: accountId ? storedFamilyStudentId(accountId) : null,
    });
  }, [accountId, selection.accountId]);

  const list = people.data?.students ?? [];
  const chosen = selection.accountId === accountId ? selection.studentId : null;

  /**
   * A named child in the address is a *choice being made*, not a lock.
   *
   * Preferring it on every render made the switcher look broken: after
   * following a bell to `/home?student=b`, pressing the other child selected
   * them and the screen carried on showing B, because the parameter was still
   * there and still won. So it is applied once, as though the parent had
   * pressed that child themselves, and then removed from the address — which
   * is also what makes the back button behave, since the entry it replaces no
   * longer names a child.
   *
   * Only ever resolves to somebody this account is actually linked to, so the
   * parameter still widens nothing.
   */
  useEffect(() => {
    if (!requestedStudentId || !accountId || !people.data) return;
    if (!people.data.students.some((person) => person.id === requestedStudentId)) return;
    setSelection({ accountId, studentId: requestedStudentId });
    setSearchParams(
      (params) => {
        const next = new URLSearchParams(params);
        next.delete("student");
        return next;
      },
      { replace: true },
    );
  }, [requestedStudentId, accountId, people.data, setSearchParams]);

  const selectedStudent =
    (chosen !== null ? list.find((person) => person.id === chosen) : undefined) ??
    list[0] ??
    null;
  const studentId = selectedStudent?.id ?? null;
  const appearance = useStudentAppearance(studentId);

  useEffect(() => {
    if (!people.data || !accountId || selection.accountId !== accountId) return;
    rememberFamilyStudentId(accountId, studentId);
  }, [people.data, accountId, selection.accountId, studentId]);

  if (!me) return null;

  const lessonsEnabled = me.booking.studentFeatureEnabled ?? me.booking.student;
  const bookingEnabled = familyBookingEnabled(
    Boolean(lessonsEnabled && me.booking.student),
    selectedStudent ?? null,
  );
  const onAccount = pathname === "/account";
  const onHelp = pathname === "/help" || pathname.startsWith("/help/");
  const onAccountWideRoute = onAccount || pathname === "/setup"
    || pathname === "/inbox" || pathname.startsWith("/inbox/")
    || pathname === "/notifications" || pathname.startsWith("/notifications/")
    || pathname === "/invoices" || pathname.startsWith("/invoices/")
    || pathname === "/concert-invitations" || pathname.startsWith("/concert-invitations/");
  const customColours = hasCustomStudentColours(appearance.preferences);
  const signOut = () => {
    forgetFamilyStudentId(me.user.id);
    onSignOut();
  };

  return (
    <BugReporterProvider key={me.user.id} studentId={studentId}>
    <NeckDiagramPlaybackProvider key={accountId} enabled={!neckCapabilities.loading && neckCapabilities.data?.canPlay === true}>
    <NeckDiagramStudentView studentId={studentId}>
    <InboxUnreadProvider>
    <NotificationsProvider>
    <PracticeLearnerContext.Provider value={studentId}>
    <InboxReplyDraftProvider key={me.user.id}>
    <div
      className={`shell family-shell${customColours ? " has-student-colours" : ""}`}
      data-student-spacing={appearance.preferences.textSpacing}
      style={studentAccessibilityStyle(appearance.preferences)}
    >
      <Sidebar
        items={familySidebarItems(
          bookingEnabled,
          studentId !== null,
          familyHasSchoolInvoicing(list),
          lessonsEnabled,
          me.studentAwardsEnabled === true,
          neckCapabilities.data?.canCreate === true,
          me.concertInvitationsEnabled === true,
          me.groupActivitiesEnabled === true,
        )}
        brand={{ title: "Wirral Music Factory", subtitle: "Student hub" }}
        headerAction={<><BugReporter /><NotificationBell /></>}
        mobileQuickItems={familyMobileShortcutItems(
          lessonsEnabled,
          bookingEnabled,
          studentId !== null,
        )}
        footer={<FamilySidebarFooter me={me} onSignOut={signOut} />}
      />

      <main className={pathname === "/practice" ? "main family-practice-main" : "main"} id="main-content" tabIndex={-1}>
        {me.studentAwardsEnabled === true && studentId && <AwardTracker key={studentId} studentId={studentId}/>}
        {pathname === "/home" && <FamilyPurchaseNotice key={me.user.id} accountId={me.user.id} />}
        {pathname === "/home" && list.length > 0 && <p><Link className="student-home-utility-link" to="/link-child">Link my child</Link></p>}
        {pathname === "/home" && me.booking.householdOverview === true && list.length > 1 && (
          <p><Link to="/household">View household overview</Link></p>
        )}
        {!onHelp && people.error && <div className="error">{people.error}</div>}

        {!onAccount && !onHelp && pathname !== "/setup" && !pathname.startsWith("/link-child") && !people.loading && list.length === 0 && (
          <div className="panel">
            <h1>Nothing here yet</h1>
            <p className="muted">
              Continue account setup to connect a purchase to its learner or check your existing links.
            </p>
            <Link to="/setup">Continue account setup</Link>
            <p><Link className="button" to="/link-child">Link my child</Link></p>
          </div>
        )}

        {!onHelp && !onAccountWideRoute && !people.loading && list.length > 1 && !pathname.startsWith("/link-child") && pathname !== "/household" && !(pathname === "/book" && me.booking.familyCredits) && (
          <StudentSwitcher
            list={list}
            selectedId={studentId}
            onSelect={(nextStudentId) =>
              setSelection({ accountId: me.user.id, studentId: nextStudentId })
            }
          />
        )}

        {/* Keyed by student: the shell and the switcher stay mounted across a
            child change, but every screen whose subject is the selected
            student must not. Without this, a component instance is reused
            across the switch — useAsync keeps showing the previous child's
            data while it reloads, and ordinary form state (an unsaved
            Practice entry, in particular) survives long enough to be
            submitted against the wrong child once the fetch settles on the
            new one. Keying forces a clean remount instead. */}
        <FamilyMetronomeBoundary studentId={studentId}>
          <Suspense fallback={<p role="status">Loading page…</p>}>
          <Routes>
            <Route path="/help/*" element={<Suspense fallback={<p role="status">Loading help…</p>}><HelpRoutes /></Suspense>} />
            <Route path="/awards" element={<StudentAwards studentId={studentId} loadingStudent={people.loading}/>}/>
            <Route path="/setup" element={<FamilySetup />} />
            <Route path="/link-child" element={<LinkMyChild />} />
            <Route path="/link-child/confirm/:token" element={<ConfirmChildLink />} />
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route
              path="/home"
              element={
                <FamilyHome
                  key={studentId}
                  studentId={studentId}
                  booking={bookingEnabled}
                />
              }
            />
            <Route
              path="/household"
              element={
                <FamilyRouteGate
                  allowed={me.booking.householdOverview === true && list.length > 1}
                  error={people.error}
                  loading={people.loading}
                >
                  <HouseholdOverview
                    key={relationshipKey}
                    learners={list}
                    bookingConfigured={Boolean(lessonsEnabled && me.booking.student)}
                    sharedCreditsEnabled={me.booking.familyCredits === true}
                  />
                </FamilyRouteGate>
              }
            />
            <Route
              path="/book"
              element={
                <FamilyRouteGate
                  allowed={Boolean(bookingEnabled && studentId)}
                  error={people.error}
                  loading={people.loading}
                >
                  {me.booking.familyCredits ? <FamilyBooking key={me.user.id} learners={list} initialStudentId={studentId ?? ""}
                    initialCreditId={searchParams.get("credit")} onLearnerAdded={people.reload} /> : <StudentBooking key={studentId} studentId={studentId ?? ""}
                    creditId={searchParams.get("credit")}
                    creditStudentId={searchParams.get("credit_student")}
                    onClearCredit={() => setSearchParams((params) => {
                      const next = new URLSearchParams(params);
                      next.delete("credit"); next.delete("credit_student");
                      return next;
                    }, { replace: true })}
                  />}
                </FamilyRouteGate>
              }
            />
            <Route
              path="/lessons"
              element={
                <FamilyRouteGate
                  allowed={Boolean(lessonsEnabled && selectedStudent)}
                  error={people.error}
                  loading={people.loading}
                >
                  <Lessons key={studentId} studentId={studentId ?? ""} />
                </FamilyRouteGate>
              }
            />
            <Route
              path="/history"
              element={
                <FamilyRouteGate
                  allowed={Boolean(bookingEnabled && selectedStudent)}
                  error={people.error}
                  loading={people.loading}
                >
                  <History
                    key={`${me.user.id}:${studentId}`}
                    accountId={me.user.id}
                    studentId={selectedStudent?.id ?? ""}
                    studentName={selectedStudent?.fullName ?? ""}
                  />
                </FamilyRouteGate>
              }
            />
            <Route path="/neck-diagrams" element={<Suspense fallback={<p role="status">Loading diagrams…</p>}><NeckDiagrams /></Suspense>} />
            <Route path="/neck-diagrams/shared/:shareId" element={<Suspense fallback={<p role="status">Loading diagram…</p>}><SharedNeckDiagram /></Suspense>} />
            <Route path="/neck-diagrams/:id" element={<Suspense fallback={<p role="status">Loading diagram…</p>}><NeckDiagram /></Suspense>} />
            <Route
              path="/practice"
              element={
                studentId ? (
                  <>
                    <PracticeSectionNav />
                    <Practice key={studentId} studentId={studentId} />
                  </>
                ) : null
              }
            />
            <Route
              path="/practice/metronome"
              element={
                studentId ? (
                  <>
                    <PracticeSectionNav />
                    <Metronome />
                  </>
                ) : null
              }
            />
            <Route path="/practice/drums" element={studentId ? <><PracticeSectionNav/><Suspense fallback={<p role="status">Loading drum assessments…</p>}><DrumAssessments studentId={studentId}/></Suspense></> : null}/>
            <Route path="/practice-history" element={<PracticeMetrics key={studentId} studentId={studentId}/>}/>
            <Route path="/practice/pitch" element={studentId ? <><PracticeSectionNav/><Suspense fallback={<p role="status">Loading pitch practice…</p>}><PitchPractice key={studentId} studentId={studentId}/></Suspense></> : null}/>
            <Route path="/practice-leaderboard" element={<PracticeMetrics key={studentId} studentId={studentId} leaderboard/>}/>
            <Route
              path="/metronome"
              element={<Navigate to="/practice/metronome" replace />}
            />
            <Route
              path="/progress"
              element={
                studentId ? (
                  <FamilyProgress key={studentId} studentId={studentId} />
                ) : null
              }
            />
            <Route
              path="/progress/repertoire/:repertoireId/material/:resourceId"
              element={
                studentId ? (
                  <FamilyRepertoireMaterial key={studentId} studentId={studentId} />
                ) : null
              }
            />
            <Route
              path="/notes"
              element={
                studentId ? (
                  <FamilyLessonNotes key={`${me.user.id}:${studentId}`} studentId={studentId} />
                ) : null
              }
            />
            {/* Not keyed by student: an invoice is addressed to the family and
                covers every child on it, so switching child must not reload it
                as though it were one child's. */}
            <Route
              path="/invoices"
              element={
                <FamilyRouteGate
                  allowed={familyHasSchoolInvoicing(list)}
                  error={people.error}
                  loading={people.loading}
                >
                  <FamilyInvoices />
                </FamilyRouteGate>
              }
            />
            <Route
              path="/concert-invitations"
              element={
                <FamilyRouteGate
                  allowed={me.concertInvitationsEnabled === true}
                  error={people.error}
                  loading={people.loading}
                >
                  <ConcertInvitations />
                </FamilyRouteGate>
              }
            />
            <Route
              path="/activities"
              element={
                <FamilyRouteGate
                  allowed={Boolean(me.groupActivitiesEnabled === true && selectedStudent)}
                  error={people.error}
                  loading={people.loading}
                >
                  <GroupActivities
                    key={studentId}
                    learnerId={studentId ?? ""}
                    learnerName={selectedStudent?.fullName ?? "your learner"}
                  />
                </FamilyRouteGate>
              }
            />
            <Route
              path="/accessibility"
              element={
                <FamilyRouteGate
                  allowed={selectedStudent !== null}
                  error={people.error}
                  loading={people.loading}
                >
                  <Accessibility
                    key={studentId}
                    student={selectedStudent!}
                    preferences={appearance.preferences}
                    loading={appearance.loading}
                    loadError={appearance.error}
                    onApplied={appearance.apply}
                    onReload={appearance.reload}
                  />
                </FamilyRouteGate>
              }
            />
            {/* Not keyed by student: a conversation is addressed to the
                account, and several of a family's conversations are about
                different children. Switching child must not reload it. */}
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/inbox/:threadId" element={<Inbox />} />
            {/* Not keyed by student either: what happened is addressed to the
                account, whichever child it was about. */}
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/account" element={<Account />} />
            <Route
              path="*"
              element={<NotFoundRecovery to="/home" linkLabel="Return to Home" />}
            />
          </Routes>
          </Suspense>
        </FamilyMetronomeBoundary>
      </main>
    </div>
    </InboxReplyDraftProvider>
    </PracticeLearnerContext.Provider>
    </NotificationsProvider>
    </InboxUnreadProvider>
    </NeckDiagramStudentView>
    </NeckDiagramPlaybackProvider>
    </BugReporterProvider>
  );
}

export function FamilySidebarFooter({
  me,
  onSignOut,
}: {
  me: Me;
  onSignOut: () => void;
}) {
  return (
    <div className="family-sidebar-footer">
      <NavLink className="workflow-action-link" to="/account">
        {me.user.email ?? me.user.username ?? "this account"}
      </NavLink>
      <NavLink className="workflow-action-link" to="/setup">
        Account setup
      </NavLink>
      <NavLink className="workflow-action-link" to="/link-child">
        Link my child
      </NavLink>
      <button
        className="quiet small family-sidebar-sign-out"
        onClick={onSignOut}
      >
        Sign out
      </button>
    </div>
  );
}

export function StudentSwitcher({
  list,
  selectedId,
  onSelect,
}: {
  list: MyStudent[];
  selectedId: string | null;
  onSelect: (studentId: string) => void;
}) {
  return (
    <div className="family-student-switcher">
      <div className="family-student-switcher-desktop">
        <span className="small muted">Viewing student</span>
        <div
          className="family-student-options"
          role="group"
          aria-label="Viewing student"
        >
          {list.map((person) => (
            <button
              key={person.id}
              type="button"
              className={person.id === selectedId ? "" : "quiet"}
              aria-pressed={person.id === selectedId}
              onClick={() => onSelect(person.id)}
            >
              {person.isSelf ? `${person.fullName} (you)` : person.fullName}
            </button>
          ))}
        </div>
      </div>

      <label
        className="family-student-switcher-mobile"
        htmlFor="family-student-select"
      >
        <span className="small muted">Viewing student</span>
        <select
          id="family-student-select"
          value={selectedId ?? ""}
          onChange={(event) => onSelect(event.target.value)}
        >
          {list.map((person) => (
            <option key={person.id} value={person.id}>
              {person.isSelf ? `${person.fullName} (you)` : person.fullName}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}
